import React, { useEffect, useState, useCallback } from 'react'
import Table from 'core-system/Table'
import { TableRow, TableRowCell } from 'core-system/Table'
import Text from 'core-system/Text'
import { HeaderCell, TableDataType } from '../types'
import TableOperations from 'core-system/Table/TableOperations'
import styled from 'styled-components'
import { values } from 'lodash-es'
import { Segment } from 'redux/employer/employerTypes'
import AlternateButton from 'core-system/Button/AlternateButton'
import DownloadIcon from 'core-system/Icons/Actions/Download'
import fileDownload from 'js-file-download'
import { buildTableCsv, formatReportCellText } from '../helperFunction'

const TableOperationsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
  margin-top: 2rem;
`

const HeaderCellComponent = ({ text }: { text: string | number }) => (
  <TableRowCell noWrap={true}>
    <Text variant='action4'>{text}</Text>
  </TableRowCell>
)

const RowCell = ({
  text,
  customCell,
}: {
  text: string | number | unknown[]
  customCell: string | null
}) => {
  return (
    <TableRowCell style={{ padding: '1.425rem' }} noWrap={true}>
      <Text fontWeight={300} variant='action3'>
        {formatReportCellText({ text, customCell })}
      </Text>
    </TableRowCell>
  )
}

interface ReportCommuterTableProps {
  tableData: TableDataType[][]
  tableMessage: string
  isTableLoading: boolean
  headerCells: HeaderCell[]
  segmentsMap: Dictionary<Segment>
  setNoCommutersTableOperationsMessage: () => void
}

const ReportCommuterTable = ({
  tableData,
  tableMessage,
  isTableLoading,
  headerCells,
  segmentsMap,
  setNoCommutersTableOperationsMessage,
}: ReportCommuterTableProps) => {
  const [rows, setRows] = useState<TableDataType[][]>(tableData)

  useEffect(() => {
    setRows(tableData)
  }, [tableData])

  const HeaderCells = useCallback(() => {
    return headerCells.map((columnObject) => (
      <HeaderCellComponent text={columnObject.displayName} />
    ))
  }, [headerCells])

  // Creating Filters
  const tableOperations = useCallback(() => {
    const filters = []
    const searchColumnNumbers = []
    let sortColumnNumber = 0
    headerCells.forEach((columnObject, columnIndex) => {
      if (columnObject?.isFilterable) {
        filters.push({
          header: columnObject.displayName,
          checkboxes: values(segmentsMap).map((segmentObject) => ({
            label: segmentObject.name,
            checked: false,
            key: columnIndex,
            value: segmentObject.name,
          })),
        })
      }
      if (columnObject?.isSearchable) {
        searchColumnNumbers.push(columnIndex)
      }
      if (columnObject?.isSortable) {
        sortColumnNumber = columnIndex
      }
    })
    return { filters, searchColumnNumbers, sortColumnNumber }
  }, [headerCells, segmentsMap])

  const handleDownloadReport = () => {
    fileDownload(buildTableCsv(headerCells, rows), 'report.csv')
  }

  return (
    <>
      <TableOperationsContainer>
        <TableOperations
          rows={tableData}
          setRows={setRows}
          filters={tableOperations().filters}
          searchColumnNumbers={tableOperations().searchColumnNumbers}
          sortColumnNumber={tableOperations().sortColumnNumber}
          setNoCommutersTableMessage={setNoCommutersTableOperationsMessage}
          trailingActions={
            <AlternateButton
              startIcon={<DownloadIcon />}
              onClick={handleDownloadReport}
              disabled={isTableLoading || rows.length === 0}
            >
              Download Report
            </AlternateButton>
          }
        />
      </TableOperationsContainer>
      <Table
        headerCells={HeaderCells()}
        rows={rows.map((row) => (
          <TableRow>
            {row.map((cellObject) => (
              <RowCell
                text={cellObject.text}
                customCell={cellObject.customCell}
              />
            ))}
          </TableRow>
        ))}
        maxRows={10}
        emptyMessage={tableMessage}
        isLoading={isTableLoading}
        stickyColumns={3}
      />
    </>
  )
}

export default ReportCommuterTable
